<template>
  <callout v-if="error" type="error">
    <div>{{ error }}</div>
  </callout>
  <div class="plan-actions">
    <!-- anything from parent -->
    <slot />

    <!-- remove or reschedule CTA, both only show if && removeButton is enabled -->
    <a
      v-if="groupingId && removeButton"
      :href="link.value"
      class="btn plan-view linked-to-complete -link"
      :class="{
        disabled: isCompleteData,
      }"
      @click="disabledClick"
      >{{ link.label }}</a
    >

    <button
      v-if="!groupingId && removeButton"
      type="button"
      class="btn plan-view linked-to-complete -link"
      :class="{
        disabled: isCompleteData,
      }"
      @click.prevent="deleteIndependentItem"
    >
      {{ deleteLabel }}
    </button>

    <!-- complete -->
    <scheduled-complete-button
      v-if="completeButton"
      :scheduled-id="scheduledId"
      :entity-id="entityId"
      :entity-type="entityType"
      :entity-category="entityCategory"
      :tracker-defaults="trackerDefaults"
      :is-complete="!!isCompleteData"
      :disabled="completeActionsDisabled"
      @complete-updated="toggleComplete"
    />
  </div>
</template>

<script>
/**
 * Reschedule/Remove button and Mark Complete button for workouts
 *
 * Used in 3 ways:
 *
 * 1: calendar day view: schedulable remove, complete  Play Video passed in slot.
 * 2: dashboard: recently missed workouts: independent workout: 2 buttons, remove / mark complete
 * 3: dashboard: recently missed workouts: program workout: 2 buttons, reschedule/mark complete
 *
 */
import { defineComponent } from 'vue'
import { mapState } from 'pinia'
import { useCalendarStore } from '@/stores/calendar'
import ScheduledCompleteButton from '@/components/buttons/completes/scheduled-complete-button.vue'
import { fbApi } from '@/utils/http-api'
import Callout from '@/components/notifications/callout.vue'

export default defineComponent({
  name: 'ScheduledInstanceActions',
  components: { Callout, ScheduledCompleteButton },
  props: {
    scheduledId: {
      type: Number,
      default: null,
    },
    scheduleDate: {
      type: String,
      default: null,
    },
    // Used for state.completes
    entityId: {
      type: Number,
      required: true,
    },
    entityType: {
      default: 'workout',
      type: String,
      validator: (val) =>
        ['plan', 'workout', 'custom_workout', 'article', 'wellness_video'].includes(val),
    },

    // only used for articles / recipes currently
    entityCategory: {
      default: '',
      type: String,
      validator: (val) => ['', 'recipe'].includes(val),
    },

    groupingId: {
      type: Number,
      default: null,
    },
    isComplete: {
      type: Boolean,
      default: false,
    },
    removeButton: {
      type: Boolean,
      default: true,
    },

    // doesn't show if future
    completeButton: {
      type: Boolean,
      default: true,
    },

    // workouts { minutes, difficulty }
    // other trackers in the future
    trackerDefaults: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isCompleteData: this.isComplete,
      deleting: false,
      deleteLabel: 'Remove',
      deleteStep: 0,
      error: '',
    }
  },
  computed: {
    ...mapState(useCalendarStore, ['completeActionsDisabled']),

    link() {
      // for workouts part of programs, reschedule, for individual remove
      return {
        label: 'Reschedule',
        value: `/my/calendar/reschedule/${this.scheduleDate}?groupingId=${this.groupingId}`,
      }
    },
  },

  methods: {
    toggleComplete(state) {
      this.isCompleteData = state
    },

    disabledClick(event) {
      if (this.isCompleteData) {
        event.preventDefault()
      }
    },

    async deleteIndependentItem() {
      // disable double fetch or deleteing completed item
      if (this.deleting || this.isCompleteData) {
        return
      }

      if (this.deleteStep === 0) {
        this.deleteStep = 1
        this.deleteLabel = 'Confirm'
        return
      }

      // reset
      this.deleting = true
      this.deleteLabel = 'Removing...'
      this.error = ''

      try {
        await fbApi.delete(`/calendar/schedule/${this.scheduledId}`, {
          skipErrorModal: true, // handled on page
        })

        // hack to reload the page because the workout list is not powered by vue
        // and we needed to keep current-state dashboard working after we retired the legacy delete endpoint
        window.location.reload()
      } catch (e) {
        console.error(e)
        // default msg
        this.deleteStep = 0
        this.deleting = false
        this.deleteLabel = 'Remove'
        this.error = 'Error deleting item, try again or refresh page'
      }
    },
  },
})
</script>
